import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Stethoscope, Building2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BrandMark } from "@/components/AppShell";
import Footer from "@/components/Footer";
import TrustBadge from "@/components/TrustBadge";
import HcahpsStars from "@/components/HcahpsStars";
import api from "@/lib/api";

const CITIES = ["Delhi", "Mumbai", "Bengaluru", "Chennai", "Hyderabad", "Kolkata", "Pune", "Ahmedabad", "Kochi"];
const SPECIALTIES = [
  "Cardiologist", "Neurologist", "Oncologist", "Orthopedician",
  "Pediatrician", "Gastroenterologist", "Dermatologist",
  "Pulmonologist", "Psychiatrist", "General Physician",
  "Ophthalmologist", "Endocrinologist",
];

export default function Directory() {
  const [tab, setTab] = useState("doctors");
  const [q, setQ] = useState("");
  const [city, setCity] = useState("all");
  const [specialty, setSpecialty] = useState("all");
  const [minTrust, setMinTrust] = useState(0);
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { min_trust: minTrust };
      if (q) params.q = q;
      if (city !== "all") params.city = city;
      if (tab === "doctors") {
        if (specialty !== "all") params.specialty = specialty;
        const { data } = await api.get("/doctors", { params });
        setDoctors(data);
      } else {
        const { data } = await api.get("/hospitals", { params });
        setHospitals(data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tab, city, specialty, minTrust]);

  const trustOptions = useMemo(
    () => [
      { v: 0, l: "Any Trust score" },
      { v: 65, l: "Silver+ (65+)" },
      { v: 78, l: "Gold+ (78+)" },
      { v: 88, l: "Platinum (88+)" },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" className="hover:bg-primary/5">Sign in</Button></Link>
            <Link to="/register"><Button className="rounded-md">Get started</Button></Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="max-w-2xl">
          <div className="overline">Verified Directory</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl text-primary">
            Discover trusted providers.
          </h1>
          <p className="mt-3 text-muted-foreground">
            Every entity below is scored across 11 objective criteria and tiered Bronze → Platinum.
          </p>
        </div>

        <div className="mt-8 rounded-lg border border-border bg-white p-4 md:p-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-5">
              <label className="overline">Search</label>
              <div className="mt-1 relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder={tab === "doctors" ? "Search doctors by name…" : "Search hospitals by name…"}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  data-testid="directory-search-input"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="overline">City</label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger className="mt-1" data-testid="directory-city-select"><SelectValue placeholder="Any city" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cities</SelectItem>
                  {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {tab === "doctors" && (
              <div className="md:col-span-2">
                <label className="overline">Specialty</label>
                <Select value={specialty} onValueChange={setSpecialty}>
                  <SelectTrigger className="mt-1" data-testid="directory-specialty-select"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All specialties</SelectItem>
                    {SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={tab === "doctors" ? "md:col-span-2" : "md:col-span-4"}>
              <label className="overline">Trust</label>
              <Select value={String(minTrust)} onValueChange={(v) => setMinTrust(Number(v))}>
                <SelectTrigger className="mt-1" data-testid="directory-trust-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {trustOptions.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList data-testid="directory-tabs">
            <TabsTrigger value="doctors" data-testid="tab-doctors"><Stethoscope size={14} className="mr-1.5" /> Doctors</TabsTrigger>
            <TabsTrigger value="hospitals" data-testid="tab-hospitals"><Building2 size={14} className="mr-1.5" /> Hospitals</TabsTrigger>
          </TabsList>

          <TabsContent value="doctors" className="mt-6">
            {loading ? (
              <SkeletonGrid />
            ) : doctors.length === 0 ? (
              <EmptyState label="No doctors match these filters." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="doctors-list">
                {doctors.map((d) => <DoctorCard key={d.id} doctor={d} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="hospitals" className="mt-6">
            {loading ? (
              <SkeletonGrid />
            ) : hospitals.length === 0 ? (
              <EmptyState label="No hospitals match these filters." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hospitals-list">
                {hospitals.map((h) => <HospitalCard key={h.id} hospital={h} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

function DoctorCard({ doctor }) {
  return (
    <Link
      to={`/doctors/${doctor.id}`}
      className="group block rounded-lg border border-border bg-white p-5 hover:border-primary transition-colors"
      data-testid={`doctor-card-${doctor.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl text-primary">{doctor.name}</h3>
          <p className="text-sm text-muted-foreground">{doctor.specialty} · {doctor.experience_years}y exp.</p>
        </div>
        <TrustBadge tier={doctor.tier} score={doctor.trust_score} />
      </div>
      <div className="mt-4 space-y-1.5 text-sm">
        <div className="flex items-center gap-1.5 text-muted-foreground"><MapPin size={13} /> {doctor.city}</div>
        <div className="flex items-center gap-1.5 text-muted-foreground truncate"><Building2 size={13} /> {doctor.hospital_affiliation || "—"}</div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">₹ {doctor.consultation_fee || 500} · consult fee</span>
        <span className="text-primary text-xs font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          View profile <ArrowRight size={12} />
        </span>
      </div>
    </Link>
  );
}

function HospitalCard({ hospital }) {
  return (
    <Link
      to={`/hospitals/${hospital.id}`}
      className="group block rounded-lg border border-border bg-white p-5 hover:border-primary transition-colors"
      data-testid={`hospital-card-${hospital.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl text-primary">{hospital.name}</h3>
          <p className="text-sm text-muted-foreground">{hospital.type}</p>
        </div>
        <TrustBadge tier={hospital.tier} score={hospital.trust_score} />
      </div>
      {hospital.hcahps_stars ? (
        <div className="mt-3 flex items-center gap-2">
          <HcahpsStars stars={hospital.hcahps_stars} size={12} testid={`hospital-card-hcahps-${hospital.id}`} />
          <span className="text-[11px] text-muted-foreground">
            HCAHPS · <span className="text-primary font-semibold">{hospital.hcahps_score}</span>/100
          </span>
        </div>
      ) : null}
      <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
        <div><div className="text-lg font-semibold text-primary">{hospital.beds}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Beds</div></div>
        <div><div className="text-lg font-semibold text-primary">{hospital.city}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">City</div></div>
        <div><div className="text-lg font-semibold text-primary">{(hospital.accreditation || "").split(",")[0] || "—"}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Accred.</div></div>
      </div>
      <div className="mt-4 flex items-center justify-end">
        <span className="text-primary text-xs font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          Explore <ArrowRight size={12} />
        </span>
      </div>
    </Link>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-40 rounded-lg border border-border bg-white animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}
